import { Button } from "components/Button";
import { useContext, useRef, useState } from "react";
import { EquipmentListItem } from "types/EquipmentListItem";
import { EquipmentEditSubmitItem } from "types/EquipmentEditSubmitItem";
import { useEquipmentDetails } from "./useEquipmentDetails";
import { Equipment } from "./Equipment";
import Form from "@rjsf/core";
import { ErrorSummary } from "components/ErrorSummary";
import { submitByRefPromise } from "components/JsonForm/utils";
import { Offcanvas, Spinner, Stack } from "react-bootstrap";
import { omit } from "lodash";
import { SubmitButton } from "components/Button/SubmitButton";
import { EquipmentBlockContext } from "components/utils/contexts";

export const EquipmentEdit = ({ item, readonly }: EquipmentEditProps) => {
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [errorSummary, setErrorSummary] = useState<any>();
    const formRef = useRef<Form<any>>();

    const { applicationNumber, onEquipmentEditSubmit, onEquipmentEditClose } = useContext(EquipmentBlockContext);
    const [equipment, isLoading] = useEquipmentDetails(applicationNumber, item.equipid);

    const onSubmit = async () => {
        if (readonly) {
            return;
        }

        try {
            // Validate equipment form
            const formData = await submitByRefPromise(formRef);
            const equipmentNumber = item.equipid;
            const catalogNumber = equipment.catalogNumber;
            const hasApprovedMeasure = equipment.attributes.some((attr) => attr.hasApproveMeasure);
            if (catalogNumber && equipmentNumber) {
                const equipmentItem: EquipmentEditSubmitItem = {
                    catalogNumber,
                    quantity: formData.quantity,
                    attributes: Object.keys(omit(formData, "quantity")).map((key: string) => ({
                        attributename: key,
                        attributevalue: String(formData[key] ?? ""),
                    })),
                };

                setIsSubmitting(true);

                if (onEquipmentEditSubmit) {
                    await onEquipmentEditSubmit(equipmentNumber, equipmentItem, hasApprovedMeasure);
                }
            }
        } catch (error) {
            setErrorSummary(error);
            setIsSubmitting(false);
        }
    };

    return (
        <Offcanvas
            className="equipment-panel"
            show
            placement="end"
            backdrop="false"
            onHide={onEquipmentEditClose}
            aria-labelledby="edit-equipment-title"
        >
            <Offcanvas.Header closeButton>
                <Offcanvas.Title id="edit-equipment-title">Edit Equipment</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body className="d-flex flex-column">
                {isLoading ? (
                    <Spinner className="align-self-center" animation="border" role="status">
                        <span className="visually-hidden">Loading equipment...</span>
                    </Spinner>
                ) : (
                    <>
                        <ErrorSummary errorSummary={errorSummary} />
                        {equipment && <Equipment formRef={formRef} item={equipment} readonly={readonly} />}
                        <Stack className="equipment-controls mt-auto justify-content-end" direction="horizontal">
                            {equipment && !readonly && (
                                <SubmitButton
                                    onClick={onSubmit}
                                    isSubmitting={isSubmitting}
                                    spinnerText="Updating equipment..."
                                    className="me-3"
                                >
                                    Save
                                </SubmitButton>
                            )}
                            <Button variant="secondary" onClick={onEquipmentEditClose} disabled={isSubmitting}>
                                {readonly ? "Close" : "Cancel"}
                            </Button>
                        </Stack>
                    </>
                )}
            </Offcanvas.Body>
        </Offcanvas>
    );
};

interface EquipmentEditProps {
    item: EquipmentListItem;
    readonly: boolean;
}
